import { Button, Chip, Link as HeroLink, Tooltip } from '@heroui/react';
import React, { useState, useEffect, useCallback } from 'react';
import { FiMessageSquare } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import { ErrorCard } from '../../../../components/ErrorCard';
import { PageLoading } from '../../../../components/PageLoading';
import { useGetBuyer } from '../../hooks';
import { getBuyerStatusPresentation } from '../../utils';
import { BuyerProfileTabs } from '../BuyerProfileTabs';

import { ProfileTab } from './ProfileTab';
import { SettingsTab } from './SettingsTab';
import { TeamTab } from './TeamTab';

export const BuyerProfile: React.FC = () => {
  const [activeTab, setActiveTab] = useState('profile');

  const { buyer, isLoading: loading, error } = useGetBuyer();

  useEffect(() => {
    if (buyer?.name) {
      document.title = `${buyer.name} - Buyer Profile - Choco Admin`;
    }
  }, [buyer?.name]);

  const renderTabContent = useCallback(() => {
    if (!buyer) return null;

    switch (activeTab) {
      case 'profile':
        return <ProfileTab buyer={buyer} />;
      case 'settings':
        return <SettingsTab buyer={buyer} />;
      case 'team':
        return <TeamTab buyer={buyer} />;
      default:
        return <ProfileTab buyer={buyer} />;
    }
  }, [activeTab, buyer]);

  if (loading) {
    return <PageLoading text="Loading buyer profile..." />;
  }

  if (error || !buyer) {
    return (
      <ErrorCard
        title="Error Loading Buyer"
        message={
          error instanceof Error
            ? error.message
            : 'Buyer not found or could not be loaded'
        }
        backLinkTo="/buyers"
        backLinkText="← Back to Buyers"
      />
    );
  }

  return (
    <div className="p-6 min-h-screen bg-background">
      <div className="mb-6">
        <HeroLink
          as={Link}
          to="/buyers"
          color="primary"
          className="inline-flex items-center gap-2 mb-4"
        >
          ← Back to Buyers
        </HeroLink>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="title-large mb-2">{buyer.name}</h1>
            <div className="flex items-center gap-2">
              <Chip
                variant="flat"
                color="default"
                size="sm"
                classNames={{
                  base: 'bg-default-100 text-default-700',
                  content: 'font-mono text-xs p-2',
                }}
              >
                {buyer.referenceId || 'N/A'}
              </Chip>
              {buyer.status && (
                <Tooltip
                  placement="bottom"
                  content={
                    <p className="p-3 max-w-xs text-xs text-foreground">
                      {getBuyerStatusPresentation(buyer.status).description}
                    </p>
                  }
                  classNames={{
                    base: 'max-w-xs',
                    content: 'bg-content1 border border-default-200',
                  }}
                >
                  {/* A button, not a span: the trigger has to be focusable for
                      the tooltip to open on hover and on keyboard focus. */}
                  <button
                    type="button"
                    className="inline-flex cursor-help"
                    aria-label={`Status: ${
                      getBuyerStatusPresentation(buyer.status).label
                    }. ${getBuyerStatusPresentation(buyer.status).description}`}
                  >
                    <Chip
                      variant="flat"
                      color={getBuyerStatusPresentation(buyer.status).color}
                      size="sm"
                      data-testid="buyer-status-chip"
                    >
                      {getBuyerStatusPresentation(buyer.status).label}
                    </Chip>
                  </button>
                </Tooltip>
              )}
              {buyer.isTest && (
                <Chip variant="flat" color="warning" size="sm">
                  Test Buyer
                </Chip>
              )}
            </div>
          </div>
          <Button
            as={Link}
            to={{
              pathname: '/chats',
              search: `?search=${encodeURIComponent(
                buyer.id
              )}&type=all&enabledState=enabled`,
            }}
            variant="bordered"
            startContent={<FiMessageSquare size={16} />}
          >
            Show Chats
          </Button>
        </div>
      </div>

      <BuyerProfileTabs activeTab={activeTab} onTabChange={setActiveTab} />

      <div className="mt-6">{renderTabContent()}</div>
    </div>
  );
};
